/**
 * Détection d'une durée dans le texte libre d'une étape (US-17, étendue par
 * US-78).
 *
 * Le mode cuisine ne proposait un minuteur que si `step.duration_min` était
 * renseigné à la main. Sur une recette importée — donc sur la plupart —, ce
 * champ est vide, et « Mijoter 30 minutes à feu doux » n'offrait aucun
 * minuteur alors que la durée est écrite en toutes lettres (audit externe,
 * constat 05). La qualité de l'écran le plus important ne doit pas dépendre
 * d'une corvée de saisie.
 *
 * **Règle directrice : ne jamais deviner.** Le §3.2 l'impose déjà au parseur
 * d'ingrédients (« ne jamais deviner une quantité ») ; c'est encore plus vrai
 * ici, où une valeur fausse fait brûler un plat. Dans le doute, on ne propose
 * rien — l'utilisateur garde le champ « Durée » de l'écran d'édition.
 *
 * US-78 durcit cette règle en trois points (D12), parce que le résultat cesse
 * d'alimenter une simple proposition à l'écran : il est **écrit en base** à
 * l'enregistrement, par `buildSteps` et par `PUT .../steps`.
 *
 *   · **Deux durées dans une étape → rien.** « Faire revenir 5 min puis
 *     mijoter 30 min » ne peut pas armer un bouton de minuteur juste — ni 5,
 *     ni 30, ni leur somme. Avant US-78, la première l'emportait.
 *   · **Une durée « à l'avance » n'est pas la durée de l'étape.**
 *     « Préchauffer 10 min à l'avance » dit quand commencer, pas combien de
 *     temps dure l'étape.
 *   · **Les formes en lettres sont lues** : « une demi-heure », « un quart
 *     d'heure », « trois quarts d'heure », « une heure et demie ». La lacune
 *     était connue depuis US-59 — l'utilisateur croyait la reconnaissance
 *     vocale en panne alors qu'elle avait parfaitement marché.
 *
 * Les cas couverts avant US-78 donnent le même résultat qu'avant : c'est la
 * première exigence de la suite de tests, parce que cette fonction est déjà
 * branchée sur le mode cuisine.
 *
 * ### US-98 : la seconde existe, et elle est l'unité
 *
 * **Cette fonction renvoie des secondes.** Elle renvoyait des minutes de
 * US-17 à US-98, ce qui allait de soi tant que le corpus était français et
 * familial — on n'écrit pas « 20 secondes » dans une blanquette. Les deux
 * cents recettes Thermomix du fonds l'ont retourné : « Mixer 20 sec /
 * vitesse 5 » est **la** phrase la plus fréquente d'une recette d'appareil, et
 * 329 étapes portaient une durée en secondes que ces motifs ne lisaient pas.
 * Aucun minuteur, alors que la durée est écrite en toutes lettres — le défaut
 * même qu'US-17 existait pour corriger, sur un corpus qu'elle n'avait pas vu.
 *
 * Arrondir n'était pas une option : « 20 sec » vers une minute, c'est un blanc
 * d'œuf monté en beurre. La minute n'est pas la bonne unité de stockage, elle
 * était seulement la bonne unité **de l'époque**.
 *
 * Le renommage — `extractDurationFromText` → `extractDureeSecondes` — est
 * délibéré et il est le filet : changer l'unité sans changer le nom aurait
 * laissé chaque appelant compiler en lisant des secondes là où il attendait
 * des minutes, c'est-à-dire un minuteur soixante fois trop court sans une
 * seule erreur. Le nom force la relecture de chaque site d'appel.
 */

/**
 * Bornes de plausibilité : au-delà, c'est plus probablement un nombre mal lu.
 *
 * Le plancher passe d'une minute à **une seconde** (US-98) : « Mixer 5 sec »
 * est une instruction courante et parfaitement sensée sur un appareil. Le
 * plafond ne bouge pas — vingt-quatre heures restent le maximum au-delà duquel
 * un nombre est plus probablement une quantité mal lue qu'une durée.
 */
const MIN_SECONDES = 1;
const MAX_SECONDES = 24 * 60 * 60;

/**
 * Unités de mesure susceptibles de suivre un nombre dans une instruction.
 * Leur présence disqualifie le nombre : « 200 g », « 30 cl », « 180 °C » ne
 * sont pas des durées. Le degré est traité à part, avec ses écritures.
 */
const NON_DURATION_AFTER =
  '(?:g|kg|ml|cl|dl|l|°|degr[ée]s?|c\\.\\s*à\\s*[cs]\\.|cuill[èe]re?s?|pinc[ée]es?|gousses?|tranches?|morceaux?|parts?|personnes?|cm|mm)';

/**
 * « 1 h 30 », « 1h30 », « 2 heures », « 1 heure 15 minutes »,
 * « 1 heure et demie ».
 *
 * La lookahead `(?![a-zà-ÿ])` après le marqueur d'heure est indispensable :
 * sans elle, « 2 hachés » se lirait « 2 h » — soit deux heures de cuisson.
 */
const HOURS =
  /(\d{1,2})\s*h(?:eures?)?(?![a-zà-ÿ])\.?\s*(?:(\d{1,2})\s*(?:min\b|mn\b|minutes?\b)?)?(\s*et\s+demie?\b)?/gi;

/**
 * Fourchette : « 10 à 12 minutes », « 10-12 min ». On retient le minimum,
 * qui est le moment où il faut aller regarder — pas le maximum, qui laisserait
 * brûler.
 */
const MINUTES_RANGE = /(\d{1,4})\s*(?:à|a|-|–)\s*\d{1,4}\s*(?:min\b|mn\b|minutes?\b)/gi;

/** « 30 minutes », « 25 min », « 5 mn », « 45min ». */
const MINUTES = /(\d{1,4})\s*(?:min\b|mn\b|minutes?\b)/gi;

/**
 * Fourchette en secondes : « 20 à 30 sec ». Même règle que les minutes — on
 * retient le minimum, qui est le moment où il faut aller regarder.
 */
const SECONDES_RANGE = /(\d{1,4})\s*(?:à|a|-|–)\s*\d{1,4}\s*(?:s\b|sec\b|secs\b|secondes?\b)/gi;

/**
 * « 20 sec », « 30 secondes », « 10 s » (US-98) — la forme la plus fréquente
 * d'une recette d'appareil.
 *
 * `\b` après le `s` est ce qui empêche « 5 sachets » et « 2 sucres » de
 * devenir des durées : la frontière de mot exige un caractère non alphabétique
 * après le `s`, et « sachets » n'en a pas. Le point d'une abréviation, la
 * barre oblique de « 20 sec/vitesse 5 » et la fin de ligne en sont, eux.
 *
 * `c. à s.` est retiré du texte avant toute recherche par `stripMeasurements`
 * — sans quoi « 2 c. à s. » y laisserait un « s ».
 */
const SECONDES = /(\d{1,4})\s*(?:s\b|sec\b|secs\b|secondes?\b)/gi;

/**
 * Les formes en lettres (US-78). Uniquement celles dont la valeur est une
 * convention et non une estimation : « une demi-heure » vaut 30 minutes comme
 * « th. 6 » vaut 180 °C. « Une nuit » n'y figure pas — huit heures ou douze ?
 * — et reste sans durée tant qu'elle n'est pas écrite en heures.
 *
 * L'ordre compte : « trois quarts d'heure » doit être lu avant « quart
 * d'heure », sinon la première passe n'en lirait que la fin.
 */
const EN_LETTRES: readonly { motif: RegExp; minutes: number }[] = [
  { motif: /\btrois\s+quarts\s+d[’']heure\b/gi, minutes: 45 },
  { motif: /\b(?:une\s+)?demie?[- ]heure\b/gi, minutes: 30 },
  { motif: /\b(?:un\s+)?quart\s+d[’']heure\b/gi, minutes: 15 },
  { motif: /\bune\s+heure\s+et\s+demie?\b/gi, minutes: 90 },
  { motif: /\bune\s+heure\b/gi, minutes: 60 },
];

/**
 * Ce qui, juste après une durée, dit qu'elle n'appartient pas à cette étape :
 * « 10 min à l'avance », « 10 min d'avance », « 10 min avant la fin ». Le
 * nombre est vrai, mais il date un début ou une fin — l'armer en minuteur
 * ferait sonner l'étape au mauvais moment.
 */
const PAS_CETTE_ETAPE = /^\s*(?:[àa]\s+l[’']avance|d[’']avance|avant\s+la\s+fin)/i;

function isPlausible(secondes: number): boolean {
  return Number.isFinite(secondes) && secondes >= MIN_SECONDES && secondes <= MAX_SECONDES;
}

/**
 * Retire les fragments « nombre + unité de mesure » avant toute recherche de
 * durée, pour qu'un « 180 °C » ne puisse jamais être lu comme 180 minutes.
 */
function stripMeasurements(text: string): string {
  return text.replace(new RegExp(`\\d+(?:[.,]\\d+)?\\s*${NON_DURATION_AFTER}`, 'gi'), ' ');
}

/** Une durée trouvée dans le texte : sa valeur **en secondes** et l'endroit où elle s'arrête. */
interface Occurrence {
  secondes: number;
  fin: number;
}

function sansChevauchement(spans: readonly [number, number][], debut: number, fin: number): boolean {
  return spans.every(([s, e]) => fin <= s || debut >= e);
}

/**
 * Toutes les expressions de durée du texte, chaque passe ignorant ce qu'une
 * passe précédente a déjà consommé : dans « 1 heure 15 minutes », le
 * « 15 minutes » appartient à l'expression d'heures et ne compte pas pour une
 * seconde durée.
 */
function occurrences(cleaned: string): Occurrence[] {
  const spans: [number, number][] = [];
  const found: Occurrence[] = [];

  const collect = (motif: RegExp, valeur: (m: RegExpExecArray) => number): void => {
    motif.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = motif.exec(cleaned)) !== null) {
      const debut = m.index;
      const fin = debut + m[0].length;
      if (m[0].length === 0) {
        motif.lastIndex += 1;
        continue;
      }
      if (!sansChevauchement(spans, debut, fin)) continue;
      spans.push([debut, fin]);
      found.push({ secondes: valeur(m), fin });
    }
  };

  for (const { motif, minutes } of EN_LETTRES) {
    collect(motif, () => minutes * 60);
  }
  collect(HOURS, (m) => {
    const h = Number(m[1]);
    const min = m[2] === undefined ? 0 : Number(m[2]);
    const demie = m[3] === undefined ? 0 : 30;
    return (h * 60 + min + demie) * 60;
  });
  collect(MINUTES_RANGE, (m) => Number(m[1]) * 60);
  collect(MINUTES, (m) => Number(m[1]) * 60);
  collect(SECONDES_RANGE, (m) => Number(m[1]));
  collect(SECONDES, (m) => Number(m[1]));

  return found;
}

/**
 * Durée **en secondes** détectée dans l'instruction, ou `null` si rien de sûr
 * — et « sûr » exclut l'étape qui en nomme deux (D12).
 */
export function extractDureeSecondes(instruction: string): number | null {
  const cleaned = stripMeasurements(instruction);
  const toutes = occurrences(cleaned);

  // Deux durées : aucun minuteur ne peut être juste. Rien n'est écrit, et le
  // mode cuisine, qui relit ce même texte, ne propose rien non plus.
  if (toutes.length !== 1) return null;

  const seule = toutes[0];
  if (seule === undefined) return null;
  if (PAS_CETTE_ETAPE.test(cleaned.slice(seule.fin))) return null;
  return isPlausible(seule.secondes) ? seule.secondes : null;
}

/**
 * La durée **en secondes** à afficher pour une étape : la colonne d'abord, la
 * lecture du texte en secours — sauf si la colonne a été vidée à la main.
 *
 * Le gel de D11 vaut aussi pour l'affichage. Sans cette garde, la fiche en
 * lecture et le mode cuisine ressuscitent la valeur qu'on vient d'effacer :
 * effacée en base (`'manuel'`, `null`), mais relue dans le texte et réaffichée
 * à chaque rendu — l'utilisateur gagne en base et perd à l'écran, ce qui est
 * exactement la bataille perdue d'avance que la migration `028` devait clore.
 */
export function dureeSecondesAffichee(step: {
  duration_sec: number | null;
  duration_source: 'auto' | 'manuel' | null;
  instruction: string;
}): number | null {
  if (step.duration_sec !== null) return step.duration_sec;
  if (step.duration_source === 'manuel') return null;
  return extractDureeSecondes(step.instruction);
}

/**
 * La durée telle qu'on la **saisit** dans le champ d'une étape (US-98).
 *
 * Le champ était un `<input type="number">` étiqueté « Durée (min) ». La
 * seconde le rend intenable : en minutes, on ne peut plus écrire « 20 sec » ;
 * en secondes, régler une cuisson de trente minutes demande de taper 1800.
 * Aucune des deux unités ne peut être *l'*unité du champ, donc le champ cesse
 * d'en avoir une.
 *
 * Ce qu'il accepte :
 *
 * | saisie | secondes |
 * | --- | --- |
 * | `20 sec`, `20 s`, `20 secondes` | 20 |
 * | `3 min`, `3 mn` | 180 |
 * | `1 h 30` | 5 400 |
 * | `30` | 1 800 |
 *
 * **Un nombre nu vaut des minutes**, et c'est le seul choix qui ne trahit
 * personne : c'est ce que le champ signifiait hier, c'est ce que taperont les
 * doigts qui l'ont appris, et une recette dont les étapes se comptent en
 * secondes les écrit toujours avec leur unité — c'est le corpus qui le dit,
 * pas une préférence.
 *
 * La lecture des formes riches est déléguée à `extractDureeSecondes` : le
 * champ et le texte d'une étape doivent comprendre « 1 h 30 » de la même
 * façon, et deux lecteurs de durée divergeraient au premier cas tordu.
 */
export function parseDureeSaisie(saisie: string): number | null {
  const texte = saisie.trim();
  if (texte === '') return null;
  if (/^\d{1,4}$/.test(texte)) {
    const minutes = Number(texte);
    return isPlausible(minutes * 60) ? minutes * 60 : null;
  }
  return extractDureeSecondes(texte);
}
